import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { accountActionApi } from '@infrastructure/account-action-api';
import React, { useEffect, useState } from 'react';
import logo from '../../../../public/assets/logo.svg';
import { Alert, Box, Button, Typography } from '@mui/material';
import { centered } from '../freelancer/account/style';
import { QInput } from '../../components/common/inputs/QInput';
import { PrimaryButon } from '../../components/common/button/PrimaryButon';
import { colCenter } from '../../style/style';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { translate } from '@application/common/helpers/translate';
import { Controller, useForm } from 'react-hook-form';
import { useAppDispatch } from '@application/store/useStore';
import { resetPasswordAsync, verifyTokenAsync } from '@application/features/auth/authThunks';
import { VerifyTokenType } from '@application/common/enums/verify-token-type';
export const ChangePassword = () => {
    const [step, setStep] = useState(1);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const setup = searchParams.get('setup') === 'true';
    const [setupError, setSetupError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const resetToken = searchParams.get('token');
    const resetEmail = searchParams.get('email');
    const dispatch = useAppDispatch();
    const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm();
    const validateConfirmPassword = (confirmPassword) => {
        if (watch('password') !== confirmPassword)
            return translate('validations.passwordDonotMatch');
    };
    const [checking, setChecking] = useState(!setup);
    const [attempt, setAttempt] = useState(0);
    useEffect(() => {
        let active = true;
        setSetupError('');
        setStep(1);
        if (!resetToken || !resetEmail) {
            navigate(setup ? '/link-expired?setup=true' : '/link-expired', { replace: true });
            return;
        }
        setValue('token', resetToken.replaceAll(' ', '+'));
        setValue('email', resetEmail);
        setValue('password', '');
        setValue('confirmPassword', '');
        if (setup) {
            setChecking(false);
            return;
        }
        setChecking(true);
        dispatch(verifyTokenAsync({ email: resetEmail, token: resetToken.replaceAll(' ', '+'), type: VerifyTokenType.Reset }))
            .unwrap()
            .then(valid => {
            if (!active)
                return;
            if (valid)
                setChecking(false);
            else
                navigate('/link-expired', { replace: true });
        })
            .catch(() => {
            if (active)
                setSetupError('The link could not be checked. Please try again.');
        });
        return () => { active = false; };
    }, [resetEmail, resetToken, setup, setValue, dispatch, navigate, attempt]);
    const validatePassword = (password) => {
        if (password.length < 8)
            return 'Must contain atleast 8 characters.';
        if (!/[A-Z]+/.test(password))
            return 'Must contain atleast one uppercase letter.';
        if (!/[a-z]+/.test(password))
            return 'Must contain atleast one lowercase letter.';
        if (!/[0-9]+/.test(password))
            return 'Must contain atleast one number.';
        if (!/[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(password))
            return 'Must contain atleast one special character.';
    };
    const onSubmit = async (data) => {
        if (checking || submitting || !resetToken || !resetEmail)
            return;
        setSubmitting(true);
        setSetupError('');
        try {
            if (setup)
                await accountActionApi.post('account/account-setup', data);
            else
                await dispatch(resetPasswordAsync(data)).unwrap();
            setStep(2);
        }
        catch {
            setSetupError(setup
                ? 'The setup link could not be used. Check your password or contact your portal administrator for a new setup link.'
                : 'The password could not be saved. Check your password or request a new password reset link.');
        }
        finally {
            setSubmitting(false);
        }
    };
    return (_jsxs("form", { onSubmit: handleSubmit(onSubmit), children: [setupError && _jsxs(Alert, { severity: 'error', children: [setupError, checking && _jsx(Button, { onClick: () => setAttempt(value => value + 1), children: "Try again" })] }), checking && _jsx(Typography, { role: 'status', children: "Checking your password reset link\u2026" }), _jsx(Box, { sx: { width: '100%', ...centered, height: '100svh', px: 0.5 }, children: step === 1 ? (_jsxs(Box, { width: '386px', sx: { ...colCenter, flexDirection: 'column' }, children: [_jsx("img", { src: logo, alt: '' }), _jsx(Typography, { variant: 'h3', color: '#000000', mt: 3, children: setup ? 'Set up your account' : 'Create a new password' }), _jsxs(Typography, { variant: 'subtitle1', color: '#555555', children: ["Fill in your new password for ", resetEmail] }), _jsx(Controller, { name: 'password', control: control, defaultValue: '', rules: {
                                required: {
                                    value: true,
                                    message: translate('validations.fieldIsRequired')
                                },
                                validate: (value) => validatePassword(value)
                            }, render: ({ field }) => (_jsx(QInput, { ...field, mt: 5, type: 'password', placeholder: 'Enter new password', errormsg: errors.password ? errors.password.message : '' })) }), _jsx(Controller, { name: 'confirmPassword', control: control, defaultValue: '', rules: {
                                required: {
                                    value: true,
                                    message: translate('validations.fieldIsRequired')
                                },
                                validate: (value) => validateConfirmPassword(value)
                            }, render: ({ field }) => (_jsx(QInput, { ...field, mt: 2, type: 'password', placeholder: 'Confirm new password', errormsg: errors.confirmPassword ? errors.confirmPassword.message : '' })) }), _jsx(PrimaryButon, { mt: 24, mb: 1, type: 'submit', disabled: submitting || checking, children: "Change password" }), !setup && _jsx(Button, { onClick: () => navigate('/forgot-password'), children: "Request a new password reset link" }), _jsx(Button, { variant: 'text', size: 'large', onClick: () => navigate('/login'), fullWidth: true, sx: { fontWeight: 550 }, children: "Back to sign in" })] })) : (_jsxs(Box, { sx: { ...centered, flexDirection: 'column' }, children: [_jsx("img", { src: logo, alt: '' }), _jsx(Typography, { variant: 'h3', mt: 5, textAlign: 'center', color: 'initial', children: "Your new password has been successfully saved!" }), _jsx(Box, { width: 386, children: _jsx(PrimaryButon, { onClick: () => navigate('/login'), mt: 24, children: "Back to sign in" }) })] })) })] }));
};
